import { generatePath } from 'react-router';
import PaginationResponseDto from '../interfaces/paginationResponse.interface';
import { API_ROUTES } from '../consts';
import { MatchStatusEnum, RequestQueryBuilderParamsEnum } from '../consts/enums';
import { buildRequestParamsByType, buildOrder, buildSelect } from '../utils/request';
import { RequestParams, Sort } from '../interfaces/apiRequest.interface';
import { convertMatchCandidatesSort, formatMatchResponse } from './formatters';
import request from './index';

export type ApiPaginationQueryType = {
  accountId: string,
  positionId: string,
  query?: {
    page?: number,
    limit?: number,
    order?: Sort,
    contains?: RequestParams<string>,
    in?: RequestParams<string[]>,
    select?: string[],
  }
}

export const getMatchListByPositionId = async ({
  accountId, positionId, query,
}: ApiPaginationQueryType): Promise<PaginationResponseDto<Match>> => {
  const url = generatePath(API_ROUTES.MATCH_LIST, { accountId, positionId });
  const { data } = await request.get(url, {
    params: {
      ...buildRequestParamsByType(query?.contains || {}, RequestQueryBuilderParamsEnum.contains),
      ...buildRequestParamsByType(query?.in || {}, RequestQueryBuilderParamsEnum.in),
      order: buildOrder(query?.order || {}),
      select: buildSelect(query?.select),
      page: query?.page,
      limit: query?.limit,
    },
  });
  const {
    results, page, totalCount, limit,
  } = data;

  return {
    results,
    page,
    totalCount,
    limit,
  };
};

export const getMatch = async (
  accountId: string,
  positionId:string,
  matchId:string,
): Promise<MatchFormattedResponse> => {
  const url = generatePath(API_ROUTES.MATCH, { accountId, positionId, matchId });

  const { data } = await request.get(url);

  return formatMatchResponse(data);
};

// todo: change unknown
export const updateMatch = async (
  accountId: string,
  positionId:string,
  matchId:string,
  matchData: Partial<MatchResponse>,  // todo: change to Match Request
): Promise<unknown> => {
  const url = generatePath(API_ROUTES.MATCH, { accountId, positionId, matchId });

  const { data } = await request.patch(url, matchData);

  return data;
};

export const rejectMatch = async (
  accountId: string,
  positionId:string,
  matchId:string,
): Promise<void> => {
  const url = generatePath(API_ROUTES.MATCH_REJECT, { accountId, positionId, matchId });

  const { data } = await request.post(url);

  return data;
};

export const updateLatestTalent = async (
  accountId: string,
  positionId:string,
  matchId:string,
): Promise<unknown> => {
  const url = generatePath(API_ROUTES.UPDATE_LATEST_TALENT, { accountId, positionId, matchId });

  const { data } = await request.post(url);

  return data;
};

export const getMatchPreview = async (
  accountId: string,
  positionId: string,
  matchId: string,
): Promise<MatchPreviewResponse> => {
  const url = generatePath(API_ROUTES.MATCH_PREVIEW, { accountId, positionId, matchId });

  const { data } = await request.get(url);

  return data;
};

export const getMatchAuditLogs = async (
  accountId: string,
  positionId: string,
  matchId: string,
):Promise<HistoryLogResponse> => {
  const url = generatePath(API_ROUTES.HISTORY_MATCH, { accountId, positionId, matchId });

  const { data } = await request.get(url);

  return data;
};

const LIMIT = 10;

export const getMatchCandidates = async (
  positionId: string,
  accountId: string,
  query: ApiQuery,
):Promise<MatchCandidateResults> => {
  const url = generatePath(API_ROUTES.MATCH_CANDIDATES, { accountId, positionId });
  const { data } = await request.get(url, {
    params: {
      limit: LIMIT,
      page: query.page,
      order: buildOrder(convertMatchCandidatesSort(query.order)),
      ...buildRequestParamsByType(query.not || {}, RequestQueryBuilderParamsEnum.not),
      ...buildRequestParamsByType(query.where || {}, RequestQueryBuilderParamsEnum.exact),
      ...buildRequestParamsByType(query.contains || {}, RequestQueryBuilderParamsEnum.contains),
      ...buildRequestParamsByType(query.in || {}, RequestQueryBuilderParamsEnum.in),
    },
  });

  return data;
};

export const updateMatches = async (
  accountId: string,
  positionId:string,
  ids:string[],
  status:MatchStatusEnum,
): Promise<void> => {
  const url = generatePath(API_ROUTES.UPDATE_MATCHES, { accountId, positionId });

  const matchData = { ids, status };
  await request.patch(url, matchData);
};

export const promoteMatchCandidate = async (
  accountId:string,
  positionId: string,
  ids: string[],
  status: MatchStatusEnum,
):Promise<MatchPromoteCandidateResponse> => {
  const url = generatePath(API_ROUTES.MATCH_CANDIDATES_PROMOTE, { accountId, positionId });
  const { data } = await request.post(url, {
    ids,
    status,
  });

  return data;
};

export const rejectMatchCandidates = async (
  accountId:string,
  positionId: string,
  ids: string[],
):Promise<unknown> => {
  const url = generatePath(API_ROUTES.MATCH_CANDIDATES_REJECT, { accountId, positionId });
  const { data } = await request.post(url, {
    ids,
  });

  return data;
};

export const deleteMatchCandidate = async (
  accountId:string,
  positionId: string,
  ids: string[],
):Promise<string> => {
  const url = generatePath(API_ROUTES.MATCH_CANDIDATES, { accountId, positionId });
  const { data } = await request.delete(url, {
    data: {
      ids,
    },
  });

  return data;
};
